import { Link } from 'react-router';
import { 
  GuideLayout, 
  GuideSection, 
  GuideTip, 
  GuideGrid,
  GuideCard,
  GuideDarkSection
} from '@/components/guides/guide-layout';

const inlineLink =
  'text-brand-600 hover:text-brand-700 font-medium underline decoration-brand-300 underline-offset-2';

export function GettingRepairQuotesGuide() {
  return (
    <GuideLayout
      slug="getting-repair-quotes"
      category="Evidence"
      categoryColor="accent"
      title="Getting the Right Repair Quote"
      description="What garages should include in your repair quote and why proper itemisation matters for your claim."
      readTime="4 min"
      updatedDate="2026-01-12"
      datePublished="2026-01-10"
      reviewedFor="UK law"
      relatedGuides={[
        { slug: 'documenting-vehicle-damage', title: 'Documenting Your Vehicle Damage', readTime: '5 min' },
        { slug: 'photographing-pothole-damage', title: 'How to Photograph Pothole Damage', readTime: '6 min' },
        { slug: 'what-makes-a-valid-claim', title: 'What Makes a Valid Pothole Claim?', readTime: '5 min' },
      ]}
      atAGlance={[
        { label: 'How many quotes', value: 'One detailed quote is usually sufficient; two can strengthen your claim' },
        { label: 'Quote or invoice', value: 'Either works — an invoice is stronger as it proves the work was done' },
        { label: 'Must include', value: 'Garage details, vehicle identification, itemised parts, labour costs, VAT breakdown, and a date and reference' },
        { label: 'Choice of garage', value: 'Main dealer or independent — councils cannot insist you use the cheapest option' },
        { label: 'Pro tip', value: 'Ask the garage to note the damage is consistent with hitting a pothole or road defect' },
      ]}
      faqs={[
        {
          question: 'Do I need multiple quotes?',
          answer: 'One detailed quote is usually sufficient, but having two quotes from different garages can strengthen your claim. It shows you\'ve sought reasonable prices and protects against councils arguing you\'ve chosen an overpriced option.',
        },
        {
          question: 'Can I use a main dealer or must I use an independent garage?',
          answer: 'You can use either. Councils cannot insist you use the cheapest option — you\'re entitled to have your vehicle repaired to its pre-accident condition. However, very high main dealer prices may be scrutinised more closely.',
        },
        {
          question: 'What if I\'ve already paid for repairs?',
          answer: 'That\'s fine — submit the invoice as your evidence. An invoice is actually better than a quote as it proves the work was done. Keep the old damaged parts if possible as additional evidence.',
        },
        {
          question: 'Should I get the repairs done before claiming?',
          answer: 'You can claim with either a quote or an invoice. Getting repairs done first means you pay upfront, but provides stronger evidence. If money is tight, submit a quote and await the outcome.',
        },
        {
          question: 'Can the council reject my quote as too expensive?',
          answer: 'Councils can question unusually high quotes, but they cannot simply substitute their own preferred repair cost. If your quote is from a reputable garage and itemises the work clearly, it should be accepted.',
        },
        {
          question: 'What about VAT?',
          answer: 'Quotes and invoices should show VAT separately. You can claim the VAT-inclusive amount. If you\'re VAT-registered and can reclaim the VAT, you should only claim the net amount.',
        },
      ]}
    >
      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        }
        title="Why Your Quote Matters"
      >
        <p className="mb-4">
          Your repair quote or invoice is the foundation of your{' '}
          <Link to="/guides/what-makes-a-valid-claim" className={inlineLink}>
            financial claim
          </Link>
          . A vague or poorly 
          structured quote makes it easy for{' '}
          <Link to="/guides/understanding-rejection-reasons" className={inlineLink}>
            councils to question your costs
          </Link>
          . A detailed, professional 
          quote leaves little room for dispute.
        </p>
        <GuideTip variant="success" title="Pro tip">
          Ask the garage to explicitly note that the damage is consistent with{' '}
          <Link to="/guides/photographing-pothole-damage" className={inlineLink}>
            hitting a pothole or road defect
          </Link>
          . This adds professional credibility to your claim.
        </GuideTip>
      </GuideSection>

      <GuideDarkSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        }
        title="What a Good Quote Includes"
      >
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-3 bg-brand-900/50 rounded-xl">
            <svg className="w-5 h-5 text-success-300 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="text-white font-medium">Garage Details</p>
              <p className="text-brand-300 text-sm">Name, address, contact details, and company header</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-brand-900/50 rounded-xl">
            <svg className="w-5 h-5 text-success-300 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="text-white font-medium">Vehicle Identification</p>
              <p className="text-brand-300 text-sm">Registration number, make, model, and year</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-brand-900/50 rounded-xl">
            <svg className="w-5 h-5 text-success-300 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="text-white font-medium">Itemised Parts</p>
              <p className="text-brand-300 text-sm">Each part listed separately with description and cost</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-brand-900/50 rounded-xl">
            <svg className="w-5 h-5 text-success-300 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="text-white font-medium">Labour Costs</p>
              <p className="text-brand-300 text-sm">Hourly rate and estimated time for each task</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-brand-900/50 rounded-xl">
            <svg className="w-5 h-5 text-success-300 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="text-white font-medium">VAT Breakdown</p>
              <p className="text-brand-300 text-sm">Net amount, VAT amount, and gross total</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-brand-900/50 rounded-xl">
            <svg className="w-5 h-5 text-success-300 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="text-white font-medium">Date and Reference</p>
              <p className="text-brand-300 text-sm">Quote date and unique reference number</p>
            </div>
          </div>
        </div>
      </GuideDarkSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
        title="Typical Repair Costs"
      >
        <p className="mb-4">
          Here's what you might expect to pay for{' '}
          <Link to="/guides/documenting-vehicle-damage" className={inlineLink}>
            common pothole-related repairs
          </Link>
          {' '}(2026 prices, vary by vehicle and location):
        </p>
        <GuideGrid cols={2}>
          <GuideCard title="Tyre Replacement">
            £80 – £200 per tyre, depending on size and brand. Budget tyres are cheaper; premium brands cost more.
          </GuideCard>
          <GuideCard title="Alloy Wheel Repair">
            £50 – £100 for cosmetic refurbishment. Structural repairs or straightening: £80 – £150.
          </GuideCard>
          <GuideCard title="Alloy Wheel Replacement">
            £150 – £400+ for a single wheel. OEM wheels for premium vehicles can exceed £500.
          </GuideCard>
          <GuideCard title="Suspension Repair">
            £150 – £500 depending on components. Shock absorbers: £100–200 per corner. Control arms: £150–300.
          </GuideCard>
        </GuideGrid>
        <GuideTip variant="info">
          These are typical ranges. Your actual costs depend on your vehicle make, model, and local garage rates.
        </GuideTip>
      </GuideSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        }
        title="Common Quote Mistakes to Avoid"
      >
        <ul className="space-y-3">
          <li className="flex items-start gap-3">
            <svg className="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-surface-700"><strong>Lump sum quotes:</strong> "Repair work: £350" with no breakdown is easy to challenge</span>
          </li>
          <li className="flex items-start gap-3">
            <svg className="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-surface-700"><strong>Handwritten notes:</strong> Informal quotes look unprofessional and can be questioned</span>
          </li>
          <li className="flex items-start gap-3">
            <svg className="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-surface-700"><strong>Verbal estimates:</strong> Always get it in writing — verbal quotes have no weight</span>
          </li>
          <li className="flex items-start gap-3">
            <svg className="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-surface-700"><strong>Missing vehicle details:</strong> The quote must clearly identify your specific vehicle</span>
          </li>
        </ul>
      </GuideSection>
    </GuideLayout>
  );
}
